import React, { useState, useEffect } from "react";
import {
    Table,
    ScrollArea,
    Group,
    Text,
    Badge,
    Loader,
    SegmentedControl,
    Select,
    Button,
    Box,
    Pagination,
    TextInput
} from "@mantine/core";
import { DatePicker, DatePickerInput } from '@mantine/dates';
import { ChartBar, Search, Filter, X } from "tabler-icons-react";
import { useGetDiagnosticParentEleveNoteQuery } from "../../../redux/features/noteApi";

export default function DiagnosticNoteTable() {
    const [timeFilter, setTimeFilter] = useState('all');
    const [filteredData, setFilteredData] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [parentName, setParentName] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [availableTypes, setAvailableTypes] = useState([]);
    let [limit] = useState(5000);
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const {
        data: response,
        error,
        isLoading,
        refetch
    } = useGetDiagnosticParentEleveNoteQuery({ limit }, {
        transformResponse: (response) => response || { data: [], count: 0 }
    });
    const totalData = response?.count
    // Totaux globaux (avant filtres)
    const diagnosticsData = Array.isArray(response?.data) ? response.data : [];
    const totalDiagnostics = typeof response?.count === 'number' ? response.count : diagnosticsData.length;
    const totalParents = new Set(
        diagnosticsData
            .map(item => item?.createdBy?.id ?? item?.createdBy?.phone ?? item?.createdBy?.fullName)
            .filter(Boolean)
    ).size;
    const totalChildren = new Set(
        diagnosticsData
            .map(item => item?.student?.id ?? (item?.student?.firstName && item?.student?.lastName ? `${item.student.firstName}|${item.student.lastName}` : null))
            .filter(Boolean)
    ).size;
    // Extraire les types de diagnostic uniques pour le filtre
    useEffect(() => {
        if (response && response.data && response?.count > 0) {
            const types = [...new Set(response.data
                .filter(item => item.diagnostic && item.diagnostic.type_diagnostique)
                .map(item => item.diagnostic.type_diagnostique))];

            setAvailableTypes(types.map(type => ({ value: type, label: type })));
        }
    }, [response]);

    const filterDataByTime = (data) => {
        if (!data || data.length === 0) return [];
        const now = new Date();

        return data.filter(item => {
            const itemDate = new Date(item.date_diagnostic);

            // Filtrage par période prédéfinie
            let matchesTimeFilter = true;
            switch (timeFilter) {
                case 'today':
                    matchesTimeFilter = itemDate.toDateString() === now.toDateString();
                    break;

                case 'thisWeek': {
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - now.getDay());
                    startOfWeek.setHours(0, 0, 0, 0);
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 6);
                    endOfWeek.setHours(23, 59, 59, 999);
                    matchesTimeFilter = itemDate >= startOfWeek && itemDate <= endOfWeek;
                    break;
                }

                case 'thisMonth':
                    matchesTimeFilter = itemDate.getMonth() === now.getMonth() &&
                        itemDate.getFullYear() === now.getFullYear();
                    break;

                case 'all':
                default:
                    matchesTimeFilter = true;
            }

            // Filtrage par plage de dates
            let matchesDateRange = true;
            if (startDate || endDate) {
                if (startDate) {
                    const start = new Date(startDate);
                    start.setHours(0, 0, 0, 0);
                    if (itemDate < start) matchesDateRange = false;
                }
                if (endDate) {
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    if (itemDate > end) matchesDateRange = false;
                }
            }

            // Filtrage par nom du parent
            const matchesParentName = !parentName ||
                (item.createdBy?.fullName &&
                    item.createdBy.fullName.toLowerCase().includes(parentName.toLowerCase()));

            // Filtrage par type de diagnostic
            const matchesSelectedType = !selectedType ||
                (item.diagnostic && item.diagnostic.type_diagnostique === selectedType);

            return matchesTimeFilter && matchesDateRange && matchesParentName && matchesSelectedType;
        });
    };

    const handleResetFilters = () => {
        setTimeFilter('all');
        setStartDate(null);
        setEndDate(null);
        setParentName('');
        setSelectedType('');
        setCurrentPage(1);
    };

    useEffect(() => {
        if (response && response.data) {
            setFilteredData(filterDataByTime(response.data));
            setCurrentPage(1); // Reset to first page when filters change  
        }
    }, [response, timeFilter, startDate, endDate, parentName, selectedType]);

    // Function to get current page items
    const getCurrentPageItems = () => {
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        return filteredData.slice(indexOfFirstItem, indexOfLastItem);
    };

    if (isLoading) {
        return <Group position="center"><Loader size="lg" /></Group>;
    }

    if (error) {
        return <Text color="red" align="center">Erreur de chargement des données.</Text>;
    }

    const currentItems = getCurrentPageItems();
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    // Totaux après filtres
    const filteredDiagnosticsCount = filteredData.length;
    const filteredParentsCount = new Set(
        filteredData
            .map(item => item?.createdBy?.id ?? item?.createdBy?.phone ?? item?.createdBy?.fullName)
            .filter(Boolean)
    ).size;
    const filteredChildrenCount = new Set(
        filteredData
            .map(item => item?.student?.id ?? (item?.student?.firstName && item?.student?.lastName ? `${item.student.firstName}|${item.student.lastName}` : null))
            .filter(Boolean)
    ).size;

    const rows = currentItems.map((row) => {
        const dateTime = new Date(row?.date_diagnostic).toLocaleString("fr", {
            day: "2-digit", month: "long", year: "numeric"
        });

        const status = row?.status || "VALIDE";

        return (
            <tr key={row?.id}>
                <td><Text weight={600}>{dateTime}</Text></td>
                <td><Text size="sm">{row?.student?.firstName || "Non spécifié"} {row?.student?.lastName || ""}</Text></td>
                <td><Text size="sm">{row?.createdBy?.fullName || "Non spécifié"}</Text></td>
                <td><Text size="sm">{row?.createdBy?.phone || "Non spécifié"}</Text></td>
                <td><Text size="sm">{row?.diagnostic?.title || "Non spécifié"}</Text></td>
                <td>
                    <Badge color={status === "ENCOURS" ? "blue" : status === "VALIDE" ? "green" : "red"}>{status}</Badge>
                </td>
                <td><Text size="sm">{row?.diagnostic?.nombreDeNiveaux || "Non spécifié"}</Text></td>
                <td><Text size="sm">{row?.diagnostic?.type_diagnostique || "Non spécifié"}</Text></td>
            </tr>
        );
    });

    return (
        <>
            {/* Totaux globaux avant filtres */}
            <Box mb="sm">
                <Group spacing="xl">
                    <Group spacing={6}>
                        <Text weight={600}>Parents:</Text>
                        <Badge color="violet" variant="filled">{totalParents}</Badge>
                    </Group>
                    <Group spacing={6}>
                        <Text weight={600}>Enfants:</Text>
                        <Badge color="cyan" variant="filled">{totalChildren}</Badge>
                    </Group>
                    <Group spacing={6}>
                        <Text weight={600}>Diagnostics:</Text>
                        <Badge color="green" variant="filled">{totalDiagnostics}</Badge>
                    </Group>
                </Group>
            </Box>

            <Box mb="md">
                <Group position="apart" mb="sm">
                    <Text weight={600} size="lg">Filtres</Text>
                    <Button
                        variant="subtle"
                        rightIcon={<X size={14} />}
                        size="xs"
                        onClick={handleResetFilters}
                    >
                        Réinitialise
                    </Button>
                </Group>

                <Group position="apart" align="flex-end">
                    <Group>
                        <SegmentedControl
                            value={timeFilter}
                            onChange={setTimeFilter}
                            data={[
                                { label: "Aujourd'hui", value: 'today' },
                                { label: 'Cette semaine', value: 'thisWeek' },
                                { label: 'Ce mois', value: 'thisMonth' },
                                { label: 'Tous', value: 'all' }
                            ]}
                        />
                    </Group>

                    <Group>
                        <TextInput
                            placeholder="Rechercher par nom du parent"
                            label="Nom du parent"
                            value={parentName}
                            onChange={(e) => setParentName(e.target.value)}
                            style={{ minWidth: 200 }}
                        />
                        <DatePickerInput
                            placeholder="Date début"
                            label="Date début"
                            value={startDate}
                            onChange={setStartDate}
                            clearable
                            popoverProps={{ withinPortal: true }}
                        />
                        <DatePickerInput
                            placeholder="Date fin"
                            label="Date fin"
                            value={endDate}
                            onChange={setEndDate}
                            clearable
                            popoverProps={{ withinPortal: true }}
                        />

                        <Select
                            placeholder="Sélectionner un type"
                            label="Type de diagnostic"
                            data={[{ value: '', label: 'Tous' }, ...availableTypes]}
                            value={selectedType}
                            onChange={setSelectedType}
                            clearable
                        />
                    </Group>
                </Group>
            </Box>

            {/* Totaux après filtres */}
            <Box mb="sm">
                <Group spacing="xl">
                    <Group spacing={6}>
                        <Text weight={600}>Parents (filtrés):</Text>
                        <Badge color="violet" variant="light">{filteredParentsCount}</Badge>
                    </Group>
                    <Group spacing={6}>
                        <Text weight={600}>Enfants (filtrés):</Text>
                        <Badge color="cyan" variant="light">{filteredChildrenCount}</Badge>
                    </Group>
                    <Group spacing={6}>
                        <Text weight={600}>Diagnostics (filtrés):</Text>
                        <Badge color="green" variant="light">{filteredDiagnosticsCount}</Badge>
                    </Group>
                </Group>
            </Box>

            <ScrollArea>
                <Table horizontalSpacing="md" verticalSpacing="xs" miw={700} sx={{ tableLayout: "fixed" }} bg={"#fff"} mb={15}>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Élève</th>
                            <th>Parent</th>
                            <th>Téléphone parent</th>
                            <th>Diagnostic</th>
                            <th>Statut</th>
                            <th>Niveau</th>
                            <th>Type Diagnostic</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? rows : <tr><td colSpan={7}><Text weight={500} align="center">Pas de données disponibles</Text></td></tr>}
                    </tbody>
                </Table>
            </ScrollArea>

            <Group position="apart" mt="md">
                <Text size="sm">Total: {currentItems.length} diagnostics (page {currentPage} sur {totalPages || 1})</Text>
                {totalPages > 1 && (
                    <Pagination
                        total={totalPages}
                        page={currentPage}
                        onChange={setCurrentPage}
                        radius="md"
                    />
                )}
            </Group>
            <br></br>
        </>
    );
}